import { ActionButton, ActionButtonType, OnboardingDialog } from "@octopusdeploy/design-system-components";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { OctopusRoundTable } from "@octopusdeploy/main-navigation";
import type { ResourceCollection, LicenseStatusResource, SpaceResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { AnalyticsLinkClickedDispatcher, LinkHref } from "@octopusdeploy/portal-routes";
import { links, nameForLink, useDispatchLinkClicked } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import * as React from "react";
import type { ReactNode } from "react";
import { type AnalyticSimpleDispatcher, useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { repository } from "~/clientInstance";
import { DefaultSpaceChip } from "~/components/Chips";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import Dialog from "~/components/Dialog/Dialog";
import type { DialogControls } from "~/components/Dialog/DialogTrigger";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { SpaceLimitReachedDialogLayout } from "~/components/LicenseLimitReachedDialogLayout";
import { PageContent } from "~/components/PageContent/PageContent";
import List from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { EntitlementLimitPopUpEvent } from "~/components/UpsellDialog/upsellDialogAnalytics";
import { Note } from "~/components/form";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import Logo from "../../../../components/Logo/Logo";
import InternalRedirect from "../../../../components/Navigation/InternalRedirect/InternalRedirect";
import ListTitle from "../../../../primitiveComponents/dataDisplay/ListTitle/ListTitle";
import isWithinLicenceLimit from "../License/isWithinLicenceLimit";
import AddSpaceDialogLayout from "./AddSpaceDialogLayout";
import styles from "./style.module.less";
interface SpacesState extends DataBaseComponentState {
    spacesResponse?: ResourceCollection<SpaceResource>;
    licenseStatus?: LicenseStatusResource;
    openUpgradeDialog: boolean;
    openUpsellDialog: boolean;
    redirectTo?: LinkHref;
}
interface SpacesInternalPageProps {
    addSpaceDialogControls: DialogControls;
    dispatchAction: AnalyticSimpleDispatcher;
    dispatchLinkClicked: AnalyticsLinkClickedDispatcher;
}
class SpaceList extends List<SpaceResource> {
}
export function SpacesPage() {
    const addSpaceDialogControls = useDialogTrigger();
    const dispatchAction = useAnalyticSimpleActionDispatch();
    const dispatchLinkClicked = useDispatchLinkClicked();
    return <SpacesInternalPage addSpaceDialogControls={addSpaceDialogControls} dispatchAction={dispatchAction} dispatchLinkClicked={dispatchLinkClicked}/>;
}
class SpacesInternalPage extends DataBaseComponent<SpacesInternalPageProps, SpacesState> {
    constructor(props: SpacesInternalPageProps) {
        super(props);
        this.state = {
            openUpgradeDialog: false,
            openUpsellDialog: false,
        };
    }
    async componentDidMount() {
        return this.doBusyTask(async () => {
            const getSpacesResponse = repository.Spaces.list();
            const getLicenseStatus = repository.Licenses.getCurrentStatus();
            this.setState({
                spacesResponse: await getSpacesResponse,
                licenseStatus: await getLicenseStatus,
            });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }
        const isWithinSpaceLimit = isWithinLicenceLimit(this.state.licenseStatus, "Spaces");
        const addSpacePageAction: PrimaryPageAction = isWithinSpaceLimit
            ? {
                type: "button",
                hasPermissions: isAllowed({ permission: Permission.SpaceCreate }),
                label: "Add Space",
                onClick: this.props.addSpaceDialogControls.openDialog,
            }
            : this.upsellPageAction();
        return (<PageContent header={{ title: "Spaces", primaryAction: this.state.spacesResponse && addSpacePageAction }} busy={this.state.busy} errors={this.errors}>
                {this.state.spacesResponse && (<SpaceList initialData={this.state.spacesResponse} onRow={(item: SpaceResource) => this.buildSpaceRow(item)} onRowRedirectUrl={(item: SpaceResource) => links.spacePage.generateUrl({ currentSpaceId: item.Id })} onFilter={this.filter} filterSearchEnabled={true} apiSearchParams={["partialName"]} filterHintText="Filter by name..."/>)}
                {this.renderUpgradeDialogs()}
                {this.renderUpsellDialog(this.state.licenseStatus?.SerialNumber)}
            </PageContent>);
    }
    private upsellPageAction(): PrimaryPageAction {
        const isFreeTierEnabled = isFeatureToggleEnabled("free-tier");
        const onClickAddSpaceUpsellButton = () => {
            this.props.dispatchAction(EntitlementLimitPopUpEvent);
            this.showUpsellDialog();
        };
        return isFreeTierEnabled
            ? {
                type: "button",
                hasPermissions: isAllowed({ permission: Permission.SpaceCreate }),
                label: "Add Space",
                onClick: onClickAddSpaceUpsellButton,
            }
            : {
                type: "custom",
                key: "Unlock more spaces",
                content: (<div className={styles.spaceLimitHit}>
                          <ActionButton label="Unlock more spaces" type={ActionButtonType.Primary} onClick={() => this.showUpgradeSpacesDialog()}/>
                          <Note>You've hit the maximum number of Spaces.</Note>
                      </div>),
            };
    }
    private showUpgradeSpacesDialog() {
        this.setState({ openUpgradeDialog: true });
    }
    private showUpsellDialog() {
        this.setState({ openUpsellDialog: true });
    }
    private buildSpaceRow(space: SpaceResource): ReactNode {
        return (<div className={styles.row}>
                <div className={styles.logo}>
                    <Logo url={space.Links.Logo}/>
                </div>
                <div>
                    <ListTitle>
                        {space.Name}
                        {space.IsDefault && <DefaultSpaceChip />}
                    </ListTitle>
                    <div>{space.Description}</div>
                </div>
            </div>);
    }
    private filter(filter: string | null | undefined, resource: SpaceResource) {
        return !filter || filter.length === 0 || !resource || resource.Name.toLowerCase().includes(filter.toLowerCase()) || (resource.Description ? resource.Description.toLowerCase().includes(filter.toLowerCase()) : false);
    }
    private renderUpgradeDialogs() {
        return (<>
                <Dialog open={this.state.openUpgradeDialog}>
                    <SpaceLimitReachedDialogLayout onActionClick={() => this.setState({ openUpgradeDialog: false })}/>
                </Dialog>
                <Dialog open={this.props.addSpaceDialogControls.isOpen}>
                    <AddSpaceDialogLayout onSpaceCreated={(space) => this.setState({ redirectTo: links.spacePage.generateUrl({ currentSpaceId: space.Id }) })}/>
                </Dialog>
            </>);
    }
    private renderUpsellDialog(licenseSerialNumber?: string) {
        const upgradeLinkLabel = "Upgrade";
        const upgradePlanLink = licenseSerialNumber ? OctoLink.Create(`plan-upgrade?serial=${licenseSerialNumber}`) : OctoLink.Create("ControlCenter");
        return (<OnboardingDialog open={this.state.openUpsellDialog} title="Give teams full control with dedicated spaces" onClose={() => this.setState({ openUpsellDialog: false })} primaryButtonProps={{
                label: upgradeLinkLabel,
                external: true,
                href: upgradePlanLink,
                onClick: () => this.props.dispatchLinkClicked(nameForLink(), { linkLabel: upgradeLinkLabel, documentUrl: upgradePlanLink }),
            }} secondaryButtonProps={{ label: "Docs: Spaces", href: OctoLink.Create("spaces"), external: true }} sidePanelImage={<OctopusRoundTable />}>
                <p>The Free tier includes one space to get you started. Dedicated spaces let each team manage their own projects, deployments, and configurations without conflicts.</p>
                <p>Upgrade to access unlimited spaces.</p>
            </OnboardingDialog>);
    }
    static displayName = "SpacesInternalPage";
}
